/**
 * TrialBanner Component
 * Shows a dismissible banner for free-plan users when their trial is ending.
 * Appears when < 14 days remaining, becomes urgent (red) at < 3 days.
 * Hidden for paid plans or dismissed via sessionStorage.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, X, Zap } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCapabilities } from '@/contexts/CapabilitiesContext';

const DISMISS_KEY = 'trialBannerDismissed';

export const TrialBanner: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthContext();
  const { planId, limits } = useCapabilities();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === 'true'
  );

  const trialInfo = useMemo(() => {
    if (!isAuthenticated || !user) return null;
    if (planId !== 'free') return null; // Only show for free plan

    const trialDays = limits.trialDays || 30;

    // Use user's signupDate if available, otherwise estimate from local storage
    let signupDate: Date | null = null;

    if (user.signupDate) {
      signupDate = new Date(user.signupDate);
    } else {
      // Fallback: check localStorage for stored tier data
      try {
        const stored = localStorage.getItem('systemdox_user_tier');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.signupDate) {
            signupDate = new Date(data.signupDate);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    if (!signupDate) return null;

    const trialEnd = new Date(signupDate.getTime() + trialDays * 24 * 60 * 60 * 1000);
    const now = new Date();
    const daysRemaining = Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));

    // Only show when < 14 days remaining
    if (daysRemaining > 14) return null;

    return {
      daysRemaining,
      isUrgent: daysRemaining <= 3,
      isExpired: daysRemaining === 0,
    };
  }, [isAuthenticated, user, planId, limits.trialDays]);

  if (!trialInfo || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const { daysRemaining, isUrgent, isExpired } = trialInfo;

  const bgClass = isUrgent
    ? 'bg-red-50 border-red-200'
    : 'bg-amber-50 border-amber-200';
  const textClass = isUrgent ? 'text-red-800' : 'text-amber-800';
  const iconClass = isUrgent ? 'text-red-500' : 'text-amber-500';
  const buttonClass = isUrgent
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-amber-600 hover:bg-amber-700 text-white';

  const message = isExpired
    ? t('trial.ended')
    : t('trial.endsIn', { count: daysRemaining });

  return (
    <div className={`border-b ${bgClass} px-4 py-3`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Clock className={`w-5 h-5 flex-shrink-0 ${iconClass}`} />
          <p className={`text-sm font-medium ${textClass}`}>
            {message}{' '}
            <span className="font-normal">{t('trial.upgradeToKeepAccess')}</span>
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/upgrade')}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${buttonClass}`}
          >
            <Zap className="w-4 h-4" />
            {t('trial.upgradeNow')}
          </button>
          <button
            onClick={handleDismiss}
            className={`p-1 rounded-lg hover:bg-black/5 transition-colors ${textClass}`}
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
